import { useCallback, useReducer } from "react";

import { scrollToTop } from "@/utils/scroll";

import {
    goToStep as goToStepAction,
    nextStep as nextStepAction,
    previousStep as previousStepAction,
    reset as resetAction,
    setState as setStateAction
} from "./wizard.actions";
import { WizardReducer } from "./wizard.reducer";
import { StepMeta, WizardStateOf } from "./wizard.types";

type UseWizardReturn<T, TStepMeta> = {
    state: T;
    currentStep?: StepMeta<TStepMeta>;
    steps: Array<StepMeta<TStepMeta>>;
    nextStep: () => void;
    previousStep: () => void;
    goToStep: (step: number) => void;
    setState: (newState: Partial<T>) => void;
    canGoBack: boolean;
    canGoForward: boolean;
    reset: () => void;
};

export const useWizard = <T, TStepMeta>(
    steps: Array<StepMeta<TStepMeta>>,
    initialState: T,
    options: {
        scrollToTop: boolean;
    } = { scrollToTop: false }
): UseWizardReturn<T, TStepMeta> => {
    const [state, dispatch] = useReducer(WizardReducer, {
        state: initialState,
        currentStep: 0,
        steps: steps || [],
        history: [initialState]
    } as WizardStateOf<T, TStepMeta>);

    const scrollCheck = useCallback(() => {
        if (options.scrollToTop) {
            scrollToTop();
        }
    }, [options.scrollToTop]);

    const nextStep = useCallback(() => {
        scrollCheck();
        dispatch(nextStepAction());
    }, [scrollCheck]);

    const previousStep = useCallback(() => {
        scrollCheck();
        dispatch(previousStepAction());
    }, [scrollCheck]);

    const goToStep = useCallback(
        (step: number) => {
            scrollCheck();
            dispatch(goToStepAction(step));
        },
        [scrollCheck]
    );

    const setState = useCallback(
        (newState: Partial<T>) => dispatch(setStateAction(newState)),
        []
    );

    const canGoBack = state.currentStep > 0;
    const canGoForward = state.currentStep < state.steps.length - 1;

    const reset = useCallback(() => {
        scrollCheck();
        dispatch(resetAction({ initialState, steps }));
    }, [initialState, steps, scrollCheck]);

    return {
        state: state.state,
        currentStep: state.steps.find(
            (step) => step.index === state.currentStep
        ),
        steps: state.steps,
        nextStep,
        previousStep,
        goToStep,
        canGoBack,
        canGoForward,
        setState,
        reset
    };
};
